import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useKlantenStore, type TimelineItem, type OrderInfo, type InvoiceInfo, type ContactInfo, type EmailMessageInfo, type LeadOfferteInfo } from '../../stores/klanten-store';
import { useOrderPanelStore, type CustomerData } from '../../stores/order-panel-store';
import { useOrderDetailDrawerStore } from '../../stores/order-detail-drawer-store';
import { useOfferteDetailDrawerStore } from '../../stores/offerte-detail-drawer-store';
import { useOpportunityDetailDrawerStore } from '../../stores/opportunity-detail-drawer-store';
import { useInvoiceDetailDrawerStore } from '../../stores/invoice-detail-drawer-store';
import { computeInvoiceStatus, INVOICE_STATUS_COLORS, INVOICE_STATUS_LABELS } from '../../lib/invoice-status';
import { CallButton } from '../phone/CallButton';
import { PhoneNumber } from '../phone/PhoneNumber';
import { MaakOpvolgtaakButton } from '../phone/MaakOpvolgtaakButton';
import type { VloerkleedCartItem } from '../../lib/pricing/types';
import { OrderPanel } from '../order-panel/OrderPanel';
import { OutboundComposerModal } from '../outbound/OutboundComposerModal';
import { NewInvoiceDialog } from '../facturen/NewInvoiceDialog';
import { NewOfferteDialog, type OffertePrefillCustomer } from '../offertes/NewOfferteDialog';
import { leadStatusBadge } from '../../lib/offerte-status';
import { ContactsSection } from './ContactsSection';
import { EmailConsentDialog, type EmailConsentTarget } from './EmailConsentDialog';
import { ContactPickerDialog } from './ContactPickerDialog';
import { MissingContactDialog } from './MissingContactDialog';
import { AddContactDialog } from './AddContactDialog';
import { EditContactDialog } from './EditContactDialog';
import { DeleteContactDialog } from './DeleteContactDialog';
import { BlacklistDialog } from './BlacklistDialog';
import { MergeDialog } from './MergeDialog';
import { InkooporderWizardDialog } from './InkooporderWizardDialog';
import { AtlasInkooporderWizardDialog } from './AtlasInkooporderWizardDialog';
import { B2BRetourlabelButton } from './B2BRetourlabelButton';
import { getLastUsedContact, setLastUsedContact } from '../../lib/last-used-contact';
import {
  isB2BInkooporderAccount,
  getB2BAccountName,
  isAtlasInkooporderAccount,
  isB2BCyclusAccount,
  ATLAS_KIDTECH_ACCOUNT_NAME,
} from '../../lib/b2b-accounts';
import { afrondenEnFactureren, B2BCyclusError, isB2BCyclusOpenStatus } from '../../lib/b2b-cyclus';
import { KlantgegevensCard, type KlantgegevensCardHandle } from './KlantgegevensCard';
import { CincoPlatformTab } from './CincoPlatformSection';
import { AbonnementenSection } from './AbonnementenSection';
import { TerugkomMomentKaart } from './TerugkomMomentKaart';
import { WerkaanvragenTab } from './WerkaanvragenTab';
import { BerichtenTab } from './BerichtenTab';
import { TelefoonTab } from './TelefoonTab';
import { KlantMarketingTab } from './KlantMarketingTab';
import { TimelineItemDrawer, type TimelineDrawerTarget } from './TimelineItemDrawer';
import { ContactDrawer } from './ContactDrawer';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useCallDrawerStore } from '../../stores/call-drawer-store';
import { useUserRole } from '../../hooks/useUserRole';
import { BRAND_COLORS, ORDER_STATUS_COLORS, SF_BASE_URL } from '../../lib/order-constants';
import { OpFactuurKlantToggle } from './OpFactuurKlantToggle';
import { formatCurrency, formatDate, formatDateTime, formatEmailStatus } from '../../lib/format';

type Variant = 'page' | 'drawer';
type Tab = 'tijdlijn' | 'orders' | 'offertes' | 'doorverwijzingen' | 'werkaanvragen' | 'berichten' | 'emails' | 'facturen' | 'telefoon' | 'marketing';

interface KlantDetailViewProps {
  customerId: string;
  type: 'account' | 'lead';
  variant: Variant;
  onClose?: () => void;
}

// Group timeline items by date
function groupByDate(items: TimelineItem[]): Map<string, TimelineItem[]> {
  const groups = new Map<string, TimelineItem[]>();
  for (const item of items) {
    const dateKey = new Date(item.date).toLocaleDateString('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    if (!groups.has(dateKey)) groups.set(dateKey, []);
    groups.get(dateKey)!.push(item);
  }
  return groups;
}

// ─── SKELETON ──────────────────────────────────────────────────

function DetailSkeleton() {
  return (
    <div className="p-6 space-y-6 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-slate-200 rounded-full" />
        <div className="space-y-2">
          <div className="h-5 bg-slate-200 rounded w-48" />
          <div className="h-3 bg-slate-100 rounded w-64" />
        </div>
      </div>
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-12 bg-slate-100 rounded" />
        ))}
      </div>
    </div>
  );
}

// ─── TIMELINE TAB ──────────────────────────────────────────────

type TimelineFilter = 'all' | 'mail' | 'sms' | 'phone' | 'marketing';

function matchesTimelineFilter(item: TimelineItem, filter: TimelineFilter): boolean {
  switch (filter) {
    case 'all': return true;
    case 'sms': return item.channel === 'sms';
    case 'phone': return item.source === 'call';
    case 'marketing': return item.source === 'marketing';
    // Mail = alle e-mail/whatsapp-communicatie behalve marketing (inbox + SF-mails + e-mail-notificaties).
    case 'mail': return item.source !== 'marketing' && item.source !== 'call' && item.channel !== 'sms';
  }
}

function TimelineTab({
  timeline,
  onOpenItem,
  onOpenCall,
}: {
  timeline: TimelineItem[];
  onOpenItem: (item: TimelineItem) => void;
  onOpenCall: (callId: string) => void;
}) {
  const [filter, setFilter] = useState<TimelineFilter>('all');

  const filterChips: Array<{ id: TimelineFilter; label: string }> = [
    { id: 'all', label: 'Alles' },
    { id: 'mail', label: 'Mail' },
    { id: 'sms', label: 'SMS' },
    { id: 'phone', label: 'Telefoon' },
    { id: 'marketing', label: 'Marketing' },
  ];

  const filtered = useMemo(
    () => timeline.filter((item) => matchesTimelineFilter(item, filter)),
    [timeline, filter],
  );

  if (timeline.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-slate-400">
        <p className="text-sm">Nog geen communicatie met deze klant</p>
      </div>
    );
  }

  const grouped = groupByDate(filtered);

  const handleClick = (item: TimelineItem) => {
    if (item.source === 'call') {
      if (item.contentRef) onOpenCall(item.contentRef);
      return;
    }
    onOpenItem(item);
  };

  return (
    <div>
      {/* Kanaal-filter */}
      <div className="px-6 py-2.5 border-b border-slate-100 flex items-center gap-1 flex-wrap bg-white sticky top-[52px] z-[5]">
        {filterChips.map((chip) => (
          <button
            key={chip.id}
            onClick={() => setFilter(chip.id)}
            className={clsx(
              'px-2.5 py-1 rounded text-xs font-medium transition-colors',
              filter === chip.id ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-50',
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-slate-400">
          <p className="text-sm">Geen items in dit kanaal</p>
        </div>
      ) : (
      <div className="divide-y divide-slate-100">
      {Array.from(grouped.entries()).map(([dateLabel, items]) => (
        <div key={dateLabel}>
          <div className="px-6 py-2 bg-slate-50 sticky top-0">
            <span className="text-xs font-medium text-slate-500 capitalize">{dateLabel}</span>
          </div>
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => handleClick(item)}
              className="w-full px-6 py-3 flex items-start gap-3 text-left transition-colors hover:bg-slate-50 cursor-pointer"
            >
              {/* Channel / source icon */}
              <div className="pt-0.5 flex-shrink-0">
                {item.source === 'marketing' ? (
                  <svg className="w-4 h-4 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
                  </svg>
                ) : item.channel === 'phone' ? (
                  <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                ) : item.channel === 'whatsapp' ? (
                  <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                ) : item.channel === 'sms' ? (
                  <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                )}
              </div>

              {/* Direction arrow */}
              <div className="pt-0.5 flex-shrink-0">
                {item.isIncoming ? (
                  <svg className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m0 0l7 7m-7-7l7-7" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m0 0l-7-7m7 7l-7 7" />
                  </svg>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-slate-900 truncate">
                    {item.subject || item.fromName || '(Geen onderwerp)'}
                  </span>
                  {item.brand && (
                    <span className={clsx(
                      'text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0',
                      BRAND_COLORS[item.brand] || 'bg-slate-100 text-slate-600'
                    )}>
                      {item.brand === 'SCHOONHUISSHOP' ? 'SHS' : item.brand}
                    </span>
                  )}
                  {item.source === 'notification' && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 bg-violet-100 text-violet-700">
                      auto
                    </span>
                  )}
                </div>
                <p
                  className={clsx(
                    'text-xs truncate',
                    item.status === 'failed'
                      ? 'text-red-600'
                      : item.status === 'suppressed'
                        ? 'text-amber-600'
                        : 'text-slate-500',
                  )}
                >
                  {item.snippet}
                </p>
              </div>

              {/* Time */}
              <span className="text-xs text-slate-400 flex-shrink-0 pt-0.5">
                {new Date(item.date).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </button>
          ))}
        </div>
      ))}
      </div>
      )}
    </div>
  );
}

// ─── ORDERS TAB ────────────────────────────────────────────────

type AfrondQuickState =
  | { status: 'busy' }
  | { status: 'done'; invoiceNaam: string | null; sent: string }
  | { status: 'error'; message: string };

function OrdersTab({
  orders,
  onReorder,
  onOpenOrder,
  onOpenContact,
  cyclusAccount,
  onOrdersChanged,
}: {
  orders: OrderInfo[];
  onReorder: (orderId: string) => void;
  onOpenOrder: (orderId: string) => void;
  onOpenContact?: (contactId: string) => void;
  /** B2B-cyclusklant (Atlas-klasse): toon "Afronden + factureren" bij "Wordt gebracht". */
  cyclusAccount?: boolean;
  onOrdersChanged?: () => void;
}) {
  // B2B-cyclus: bevestigingsdialoog + per-order resultaat van de afrond-actie.
  const [confirmOrder, setConfirmOrder] = useState<OrderInfo | null>(null);
  const [afrondQuick, setAfrondQuick] = useState<Record<string, AfrondQuickState>>({});

  const handleAfrondenConfirmed = async (order: OrderInfo) => {
    setConfirmOrder(null);
    setAfrondQuick((prev) => ({ ...prev, [order.id]: { status: 'busy' } }));
    try {
      const result = await afrondenEnFactureren(order.id);
      setAfrondQuick((prev) => ({
        ...prev,
        [order.id]: {
          status: 'done',
          invoiceNaam: result.invoiceNaam,
          sent: result.steps.sent ?? 'sent',
        },
      }));
      onOrdersChanged?.();
    } catch (err) {
      const msg =
        err instanceof B2BCyclusError
          ? `${err.code}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Afronden mislukt';
      setAfrondQuick((prev) => ({ ...prev, [order.id]: { status: 'error', message: msg } }));
    }
  };

  // B2B: filter per contactpersoon zodra orders een contactpersoon dragen.
  const [contactFilter, setContactFilter] = useState<string>('');
  const contactOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of orders) {
      if (o.contactId && o.contactName) map.set(o.contactId, o.contactName);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], 'nl'));
  }, [orders]);

  const filteredOrders = contactFilter
    ? orders.filter((o) => o.contactId === contactFilter)
    : orders;

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-slate-400">
        <p className="text-sm">Geen orders gevonden</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {contactOptions.length > 0 && (
        <div className="px-6 py-2 flex items-center gap-2 bg-slate-50/60">
          <label htmlFor="orders-contact-filter" className="text-xs text-slate-500">
            Contactpersoon:
          </label>
          <select
            id="orders-contact-filter"
            value={contactFilter}
            onChange={(e) => setContactFilter(e.target.value)}
            className="px-2 py-1 border border-slate-200 rounded text-xs bg-white focus:border-indigo-400 focus:outline-none"
          >
            <option value="">Alle ({orders.length})</option>
            {contactOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          {contactFilter && (
            <span className="text-xs text-slate-400">{filteredOrders.length} orders</span>
          )}
        </div>
      )}
      {filteredOrders.map((order) => (
        <div key={order.id} className="px-6 py-4 hover:bg-slate-50 transition-colors">
          <button
            type="button"
            onClick={() => onOpenOrder(order.id)}
            className="block w-full text-left"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-900">{order.reference}</span>
                <span className={clsx(
                  'text-[10px] px-1.5 py-0.5 rounded font-medium',
                  ORDER_STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-600'
                )}>
                  {order.status}
                </span>
                {order.brand && (
                  <span className={clsx(
                    'text-[10px] px-1.5 py-0.5 rounded font-medium',
                    BRAND_COLORS[order.brand] || 'bg-slate-100 text-slate-600'
                  )}>
                    {order.brand === 'SCHOONHUISSHOP' ? 'SHS' : order.brand}
                  </span>
                )}
              </div>
              <span className="text-sm font-medium text-slate-900">{formatCurrency(order.total)}</span>
            </div>

            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>{formatDate(order.createdDate)}</span>
              {order.paymentReceived ? (
                <span className="text-emerald-600 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Betaald
                </span>
              ) : order.openAmount > 0 ? (
                <span className="text-amber-600">Openstaand: {formatCurrency(order.openAmount)}</span>
              ) : null}
              {order.pickupDate && <span>Ophalen: {formatDate(order.pickupDate)}</span>}
              {order.deliveryDate && <span>Bezorgen: {formatDate(order.deliveryDate)}</span>}
            </div>
          </button>

          {/* B2B: contactpersoon van deze order, klikt door naar de drawer */}
          {order.contactName && (
            <button
              type="button"
              onClick={() => order.contactId && onOpenContact?.(order.contactId)}
              className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600"
              title="Open contactpersoon"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
              {order.contactName}
            </button>
          )}

          {/* Opnieuw bestellen — only for completed KLEDA orders */}
          {order.status === 'Afgerond' && order.brand === 'KLEDA' && (
            <button
              onClick={() => onReorder(order.id)}
              className="mt-2 flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Opnieuw bestellen
            </button>
          )}

          {/* B2B-cyclus: afronden + factureren voor alle nog niet afgeronde orders
              (na "Markeer als gebracht" loopt een order door de wasserij-statussen) */}
          {cyclusAccount && isB2BCyclusOpenStatus(order.status) && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <B2BRetourlabelButton werkOrderId={order.id} orderReferentie={order.reference} />
              {afrondQuick[order.id]?.status === 'done' ? (
                <span className="text-xs text-emerald-700">
                  Afgerond — factuur{' '}
                  {(afrondQuick[order.id] as { invoiceNaam: string | null }).invoiceNaam ?? 'aangemaakt'}
                  {(afrondQuick[order.id] as { sent: string }).sent === 'skipped-already-sent'
                    ? ' (was al verstuurd)'
                    : ' verstuurd'}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setConfirmOrder(order)}
                    disabled={afrondQuick[order.id]?.status === 'busy'}
                    className={clsx(
                      'text-xs font-medium px-2.5 py-1 rounded',
                      afrondQuick[order.id]?.status === 'busy'
                        ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                        : 'bg-amber-600 text-white hover:bg-amber-700',
                    )}
                  >
                    {afrondQuick[order.id]?.status === 'busy' ? 'Bezig…' : 'Afronden + factureren'}
                  </button>
                  {afrondQuick[order.id]?.status === 'error' && (
                    <span className="ml-2 text-xs text-red-700">
                      {(afrondQuick[order.id] as { message: string }).message}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Bevestigingsdialoog B2B-cyclus afronden */}
      {confirmOrder && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-base font-semibold text-slate-900">Order afronden + factureren</h3>
            <p className="text-sm text-slate-600 mt-2">
              {confirmOrder.reference} · {formatCurrency(confirmOrder.total)} · status: {confirmOrder.status}
            </p>
            <p className="text-sm text-slate-600 mt-2">
              De factuur wordt aangemaakt en direct naar de klant verstuurd. De order krijgt
              daarna status Afgerond.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOrder(null)}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => void handleAfrondenConfirmed(confirmOrder)}
                className="text-sm font-medium px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700"
              >
                Afronden + factureren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── OFFERTES TAB ──────────────────────────────────────────────

function OffertesTab({
  leads,
  onOpenOfferte,
}: {
  leads: LeadOfferteInfo[];
  onOpenOfferte: (leadId: string) => void;
}) {
  if (leads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-slate-400">
        <p className="text-sm">Geen offertes gevonden</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {leads.map((lead) => {
        const badge = lead.isConverted
          ? { label: 'Geconverteerd', color: 'bg-emerald-100 text-emerald-700' }
          : leadStatusBadge(lead.status);
        const row = (
          <>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-900">
                  {lead.leadnummer || lead.name}
                </span>
                <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium', badge.color)}>
                  {badge.label}
                </span>
                {lead.brand && (
                  <span className={clsx(
                    'text-[10px] px-1.5 py-0.5 rounded font-medium',
                    BRAND_COLORS[lead.brand] || 'bg-slate-100 text-slate-600'
                  )}>
                    {lead.brand === 'SCHOONHUISSHOP' ? 'SHS' : lead.brand}
                  </span>
                )}
                {lead.koppeling === 'vangnet' && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700"
                    title="Deze offerte hangt niet aan de klant zelf, maar matcht alleen op e-mailadres of telefoonnummer. Controleer of hij echt van deze klant is."
                  >
                    Niet bevestigd
                  </span>
                )}
              </div>
              {lead.total > 0 && (
                <span className="text-sm font-medium text-slate-900">{formatCurrency(lead.total)}</span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>{formatDate(lead.createdDate)}</span>
              {lead.offerteVerstuurd && <span>Verstuurd: {formatDate(lead.offerteVerstuurd)}</span>}
              {!lead.isConverted && lead.vervaldatum && (
                <span>Vervalt: {formatDate(lead.vervaldatum)}</span>
              )}
              {lead.isConverted && lead.convertedDate && (
                <span className="text-emerald-600">Order geworden op {formatDate(lead.convertedDate)}</span>
              )}
            </div>
          </>
        );
        // Geconverteerde leads hebben geen offerte-detailpagina meer (die laadt
        // alleen ongeconverteerde leads) — toon ze als niet-klikbare historie.
        return lead.isConverted ? (
          <div key={lead.id} className="px-6 py-4">{row}</div>
        ) : (
          <button
            key={lead.id}
            type="button"
            onClick={() => onOpenOfferte(lead.id)}
            className="block w-full text-left px-6 py-4 hover:bg-slate-50 transition-colors"
          >
            {row}
          </button>
        );
      })}
    </div>
  );
}

// ─── EMAILS TAB ────────────────────────────────────────────────

type EmailFilter = 'all' | 'sent' | 'draft' | 'incoming';

function EmailsTab({
  emails,
  onOpenEmail,
  onOpenOrder,
}: {
  emails: EmailMessageInfo[];
  onOpenEmail: (id: string) => void;
  onOpenOrder: (orderId: string) => void;
}) {
  const [filter, setFilter] = useState<EmailFilter>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return emails.filter((e) => {
      if (filter === 'sent' && e.status !== 4) return false;
      if (filter === 'draft' && e.status !== 0) return false;
      if (filter === 'incoming' && !e.isIncoming) return false;
      if (q) {
        const hay = `${e.subject} ${e.toAddress} ${e.fromAddress} ${e.workOrderRef ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [emails, filter, search]);

  const filterChips: Array<{ id: EmailFilter; label: string }> = [
    { id: 'all', label: 'Alle' },
    { id: 'sent', label: 'Verzonden' },
    { id: 'draft', label: 'Concept' },
    { id: 'incoming', label: 'Ontvangen' },
  ];

  return (
    <div>
      <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap bg-white sticky top-[52px] z-[5]">
        <div className="flex items-center gap-1">
          {filterChips.map((chip) => (
            <button
              key={chip.id}
              onClick={() => setFilter(chip.id)}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                filter === chip.id
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-slate-500 hover:bg-slate-50'
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op onderwerp, adres of order…"
          className="ml-auto flex-1 min-w-[200px] max-w-[320px] px-3 py-1 border border-slate-200 rounded text-sm placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none"
        />
        <span className="text-xs text-slate-400">{filtered.length} / {emails.length}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-slate-400">
          <p className="text-sm">{emails.length === 0 ? 'Geen e-mails gevonden voor deze klant' : 'Geen e-mails matchen het filter'}</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {emails.length >= 200 && (
            <div className="px-6 py-2 bg-amber-50 text-xs text-amber-800">
              Toont laatste 200 e-mails. Oudere e-mails alleen in Salesforce zichtbaar.
            </div>
          )}
          {filtered.map((email) => {
            const badge = formatEmailStatus(email.status);
            return (
              <div
                key={email.id}
                className="px-6 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3"
              >
                <button
                  type="button"
                  onClick={() => onOpenEmail(email.id)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-slate-900 truncate">
                      {email.subject || '(Geen onderwerp)'}
                    </span>
                    <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0', badge.color)}>
                      {badge.label}
                    </span>
                    {email.hasAttachment && (
                      <svg className="w-3 h-3 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-label="Bijlage">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="truncate">
                      {email.isIncoming
                        ? <>← {email.fromAddress}</>
                        : <>→ {email.toAddress || '(geen adres)'}</>}
                    </span>
                  </div>
                </button>

                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-xs text-slate-400">
                    {formatDateTime(email.messageDate)}
                  </span>
                  {email.workOrderRef && email.relatedToId && (
                    <button
                      type="button"
                      onClick={() => onOpenOrder(email.relatedToId)}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-indigo-100 hover:text-indigo-700"
                      title="Open order"
                    >
                      {email.workOrderRef}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── INVOICES TAB ──────────────────────────────────────────────

function InvoicesTab({
  invoices,
  onOpenInvoice,
}: {
  invoices: InvoiceInfo[];
  onOpenInvoice: (id: string) => void;
}) {
  if (invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-slate-400">
        <p className="text-sm">Geen facturen gevonden</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {invoices.map((invoice) => {
        const status = computeInvoiceStatus({
          totalInclBtw: invoice.total,
          openAmount: invoice.openAmount,
          transactionTotal: invoice.transactionTotal ?? 0,
          paid: invoice.paid,
        });
        return (
          <button
            key={invoice.id}
            type="button"
            onClick={() => onOpenInvoice(invoice.id)}
            className="block w-full text-left px-6 py-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-900">{invoice.name}</span>
                <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium', INVOICE_STATUS_COLORS[status])}>
                  {INVOICE_STATUS_LABELS[status]}
                </span>
                {invoice.paymentMethod && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{invoice.paymentMethod}</span>
                )}
              </div>
              <span className="text-sm font-medium text-slate-900">{formatCurrency(invoice.total)}</span>
            </div>

            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>{formatDate(invoice.date)}</span>
              {status === 'open' && invoice.openAmount > 0 && (
                <span className="text-amber-600">Openstaand: {formatCurrency(invoice.openAmount)}</span>
              )}
              {status === 'partial' && (
                <span className="text-blue-600">
                  Deelbetaling: {formatCurrency(invoice.transactionTotal ?? 0)} / {formatCurrency(invoice.total)}
                </span>
              )}
              {invoice.workOrderRef && <span>WO: {invoice.workOrderRef}</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── MAIN VIEW ─────────────────────────────────────────────────

export function KlantDetailView({ customerId, type, variant, onClose }: KlantDetailViewProps) {
  const isDrawer = variant === 'drawer';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ?tab=facturen deep-link ondersteuning; default blijft 'tijdlijn'.
  const tabParam = searchParams.get('tab');
  const initialTab: Tab = tabParam === 'orders' || tabParam === 'offertes' || tabParam === 'doorverwijzingen' || tabParam === 'werkaanvragen' || tabParam === 'berichten' || tabParam === 'emails' || tabParam === 'facturen' || tabParam === 'telefoon' || tabParam === 'marketing'
    ? tabParam
    : 'tijdlijn';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerTo, setComposerTo] = useState('');
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);
  const [inkooporderOpen, setInkooporderOpen] = useState(false);
  const [atlasInkooporderOpen, setAtlasInkooporderOpen] = useState(false);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [missingContactDialogOpen, setMissingContactDialogOpen] = useState(false);
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [newOfferteOpen, setNewOfferteOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactInfo | null>(null);
  const [deletingContact, setDeletingContact] = useState<ContactInfo | null>(null);
  const [consentTarget, setConsentTarget] = useState<EmailConsentTarget | null>(null);
  const [blacklistDialog, setBlacklistDialog] = useState<null | 'set' | 'clear'>(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<'new' | 'reorder' | null>(null);
  const [pendingReorderItems, setPendingReorderItems] = useState<VloerkleedCartItem[] | null>(null);
  const [timelineDrawerItem, setTimelineDrawerItem] = useState<TimelineDrawerTarget | null>(null);
  // ?contact=<id> (globale zoek) opent de contact-drawer direct.
  const [drawerContactId, setDrawerContactId] = useState<string | null>(
    () => searchParams.get('contact')
  );

  const { activeCustomer, isLoadingDetail, fetchCustomerDetail, clearDetail } = useKlantenStore();
  const { openPanel, openPanelWithItems } = useOrderPanelStore();
  const submitResult = useOrderPanelStore((s) => s.submitResult);
  const openOrderDrawer = useOrderDetailDrawerStore((s) => s.open);
  const openOfferteDrawer = useOfferteDetailDrawerStore((s) => s.open);
  const openOpportunityDrawer = useOpportunityDetailDrawerStore((s) => s.open);
  const openInvoiceDrawer = useInvoiceDetailDrawerStore((s) => s.open);
  const openCall = useCallDrawerStore((s) => s.open);
  const gegevensRef = useRef<KlantgegevensCardHandle | null>(null);
  const { role } = useUserRole();
  const isAdmin = role === 'admin';

  useEffect(() => {
    if (customerId) {
      fetchCustomerDetail(customerId, type);
    }
    return () => clearDetail();
  }, [customerId, type, fetchCustomerDetail, clearDetail]);

  // Refresh orders after successful submit
  useEffect(() => {
    if (submitResult?.ok && customerId) {
      fetchCustomerDetail(customerId, type);
    }
  }, [submitResult?.ok]); // eslint-disable-line react-hooks/exhaustive-deps

  const customer = activeCustomer?.customer;
  const totalOrders = activeCustomer?.orders?.length || 0;
  const totalRevenue = activeCustomer?.orders?.reduce((sum, o) => sum + o.total, 0) || 0;

  // Bouw de CustomerData die de OrderPanel verwacht. Voor B2B gebruiken we een
  // expliciete contactpersoon (firstName/lastName + Existing_Contact__c). Voor
  // Person Accounts gebruiken we de SF-velden direct (geen split op spatie meer —
  // "Anne Marie van der Berg" splitste verkeerd).
  const buildOrderCustomer = (contact?: ContactInfo): CustomerData => {
    if (!customer) throw new Error('No customer');
    const isB2B = customer.type === 'account' && customer.isPersonAccount === false;

    // Bezorgadres heeft voorrang voor route-lookup en order-aanmaak; SF flow vult
    // ShippingX auto-fill = BillingX, dus voor klanten zonder afwijkend bezorgadres
    // verandert er niets. Alleen klanten met expliciet ander bezorgadres krijgen
    // routes op basis van het juiste adres.
    const street = customer.shippingStreet || customer.street;
    const postcode = customer.shippingPostcode || customer.postcode;
    const city = customer.shippingCity || customer.city;

    if (isB2B && contact) {
      const parts = contact.name.split(' ').filter(Boolean);
      const fName = parts[0] || '';
      const lName = parts.slice(1).join(' ') || contact.name;
      return {
        accountId: customer.id,
        firstName: fName,
        lastName: lName,
        email: contact.email || customer.email,
        phone: contact.phone || customer.phone,
        street,
        postcode,
        city,
        companyName: customer.name,
        contactId: contact.id,
      };
    }

    // Person Account: gebruik FirstName/LastName direct uit SF.
    // Fallback voor leads (geen firstName/lastName-velden): split op spatie.
    const fallbackParts = customer.name.split(' ');
    return {
      accountId: customer.id,
      firstName: customer.firstName || fallbackParts[0] || '',
      lastName: customer.lastName || fallbackParts.slice(1).join(' ') || customer.name,
      email: customer.email,
      phone: customer.phone,
      street,
      postcode,
      city,
    };
  };

  const proceedWithContact = (
    contact: ContactInfo | undefined,
    mode: 'new' | 'reorder',
    reorderItems?: VloerkleedCartItem[],
  ) => {
    if (!customer) return;
    if (contact) setLastUsedContact(customer.id, contact.id);
    const data = buildOrderCustomer(contact);
    if (mode === 'reorder' && reorderItems) {
      openPanelWithItems(data, reorderItems);
    } else {
      openPanel(data);
    }
  };

  // Centrale resolver: bepaalt of er een picker, hard-block of directe flow
  // moet starten. Person Accounts gaan altijd direct door; B2B-takken
  // hangen af van het aantal contactpersonen onder het Account.
  const resolveContactAndProceed = (mode: 'new' | 'reorder', reorderItems?: VloerkleedCartItem[]) => {
    if (!customer) return;
    const isB2B = customer.type === 'account' && customer.isPersonAccount === false;
    const contacts = activeCustomer?.contacts || [];

    if (!isB2B) {
      proceedWithContact(undefined, mode, reorderItems);
      return;
    }

    if (contacts.length === 0) {
      setMissingContactDialogOpen(true);
      return;
    }

    if (contacts.length === 1) {
      proceedWithContact(contacts[0], mode, reorderItems);
      return;
    }

    // ≥2 contacten: voor reorder eerst last-used checken
    if (mode === 'reorder') {
      const lastId = getLastUsedContact(customer.id);
      const lastContact = lastId ? contacts.find((c) => c.id === lastId) : undefined;
      if (lastContact) {
        proceedWithContact(lastContact, mode, reorderItems);
        return;
      }
    }

    setPendingMode(mode);
    setPendingReorderItems(reorderItems || null);
    setContactPickerOpen(true);
  };

  const handleNewOrder = () => resolveContactAndProceed('new');

  const handleWebshopOrder = () => {
    if (!customer) return;
    const street = customer.shippingStreet || customer.street || '';
    const postcode = customer.shippingPostcode || customer.postcode || '';
    const city = customer.shippingCity || customer.city || '';
    navigate('/nalevering', {
      state: {
        prefilledCustomer: {
          accountId: customer.id,
          name: customer.name,
          email: customer.email || '',
          phone: customer.phone || '',
          street,
          postalCode: postcode,
          city,
          isNew: false,
        },
      },
    });
  };

  const handleReorder = async (orderId: string) => {
    if (!customer) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const res = await fetch(`/api/customers/order-items?woId=${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      const cartItems: VloerkleedCartItem[] = (data.items || []).map((item: { isRound: boolean; lengthCm: number; widthCm: number; diameterCm: number }, idx: number) => ({
        id: `reorder-${idx}`,
        shape: item.isRound ? 'rond' as const : (item.lengthCm && item.widthCm ? 'rechthoekig' as const : 'overig' as const),
        lengthCm: item.lengthCm || 0,
        widthCm: item.widthCm || 0,
        diameterCm: item.diameterCm || 0,
        areaM2Manual: 0,
        selectedPackage: null,
        extras: [],
        notes: '',
        isExpanded: false,
      }));

      resolveContactAndProceed('reorder', cartItems);
    } catch (err) {
      console.error('[KlantDetail] reorder error:', err);
    }
  };

  // Contactpersonen met e-mail — bron voor de ontvanger bij zakelijke accounts,
  // waar het adres niet op het account (PersonEmail) maar op het contact staat.
  const emailContacts = useMemo(
    () => (activeCustomer?.contacts || []).filter((c) => c.email),
    [activeCustomer?.contacts],
  );
  const canSendEmail = !!customer?.email || emailContacts.length > 0;

  const handleSendEmail = () => {
    if (!customer) return;
    // Account-niveau e-mail (lead of particulier account): direct openen.
    if (customer.email) {
      setComposerTo(customer.email);
      setComposerOpen(true);
      return;
    }
    // Zakelijk account: één contact met e-mail → direct, meerdere → laten kiezen.
    const [firstContact] = emailContacts;
    if (emailContacts.length === 1 && firstContact) {
      setComposerTo(firstContact.email);
      setLastUsedContact(customer.id, firstContact.id);
      setComposerOpen(true);
      return;
    }
    if (emailContacts.length > 1) {
      setEmailPickerOpen(true);
    }
  };

  const isBusinessAccount = customer?.type === 'account' && customer?.isPersonAccount === false;

  // Prefill voor "+ Nieuwe offerte" — gememoiseerd zodat de dialog-reset-effect
  // niet op elke render opnieuw vuurt terwijl hij open staat.
  const offertePrefill = useMemo<OffertePrefillCustomer | null>(() => {
    if (!customer || customer.type !== 'account') return null;
    return {
      accountId: customer.id,
      isBusiness: customer.isPersonAccount === false,
      name: customer.name,
      firstName: customer.firstName || undefined,
      lastName: customer.lastName || undefined,
      email: customer.email || undefined,
      phone: customer.phone || customer.mobile || undefined,
      // Bezorgadres heeft voorrang, zelfde keuze als buildOrderCustomer.
      street: customer.shippingStreet || customer.street || undefined,
      postalCode: customer.shippingPostcode || customer.postcode || undefined,
      city: customer.shippingCity || customer.city || undefined,
      blacklisted: customer.blacklisted === true,
      blacklistReden: customer.blacklistReden ?? null,
    };
  }, [customer]);

  // Cinco-platform doorverwijzingen = Opportunity met Brand__c='CINCO'.
  const cincoReferrals = useMemo(
    () => (activeCustomer?.opportunities || []).filter((o) => o.brand === 'CINCO'),
    [activeCustomer?.opportunities],
  );

  const tabs: Array<{ id: Tab; label: string; count?: number }> = [
    { id: 'tijdlijn', label: 'Tijdlijn', count: activeCustomer?.timeline?.length || 0 },
    { id: 'orders', label: 'Orders', count: activeCustomer?.orders?.length || 0 },
    ...(customer?.type === 'account' ? [{
      id: 'offertes' as Tab,
      label: 'Offertes',
      count: activeCustomer?.leads?.length || 0,
    }] : []),
    ...(cincoReferrals.length > 0 ? [{
      id: 'doorverwijzingen' as Tab,
      label: 'Doorverwijzingen',
      count: cincoReferrals.length,
    }] : []),
    ...(isBusinessAccount ? [{
      id: 'werkaanvragen' as Tab,
      label: 'Werkaanvragen',
      count: activeCustomer?.werkaanvragen?.length || 0,
    }] : []),
    { id: 'berichten', label: 'Berichten', count: activeCustomer?.conversations?.length || 0 },
    { id: 'telefoon', label: 'Telefoon' },
    { id: 'emails', label: 'E-mails', count: activeCustomer?.emails?.length || 0 },
    { id: 'facturen', label: 'Facturen', count: activeCustomer?.invoices?.length || 0 },
    ...(customer?.type === 'account' ? [{ id: 'marketing' as Tab, label: 'Marketing' }] : []),
  ];

  return (
    <>
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {isLoadingDetail ? (
          <DetailSkeleton />
        ) : !customer ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <p className="text-sm">Klant niet gevonden</p>
            {isDrawer ? (
              <button onClick={onClose} className="mt-2 text-indigo-600 text-sm hover:underline">
                Sluiten
              </button>
            ) : (
              <button onClick={() => navigate('/klanten')} className="mt-2 text-indigo-600 text-sm hover:underline">
                Terug naar zoeken
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Drawer sticky header */}
            {isDrawer && (
              <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={onClose}
                  aria-label="Sluiten"
                  className="text-slate-400 hover:text-slate-600"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h2 className="text-sm font-semibold text-slate-900 truncate">{customer.name}</h2>
                <span className={clsx(
                  'text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide flex-shrink-0',
                  customer.type === 'account'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                )}>
                  {customer.type === 'account' ? 'Klant' : 'Lead'}
                </span>
                <Link
                  to={`/klanten/${customer.id}?type=${customer.type}`}
                  onClick={onClose}
                  className="ml-auto text-xs text-indigo-600 hover:underline flex-shrink-0"
                >
                  Open volledig →
                </Link>
              </div>
            )}

            {/* Blacklist-banner — prominent, geen zaken meer doen met deze klant */}
            {customer.type === 'account' && customer.blacklisted && (
              <div className={clsx(
                'bg-red-600 text-white flex items-start gap-2 flex-shrink-0',
                isDrawer ? 'px-4 py-2.5' : 'px-6 py-3'
              )}>
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <div className="text-sm leading-snug">
                  <span className="font-semibold">⛔ Blacklist — geen zaken meer doen met deze klant.</span>
                  {customer.blacklistReden && (
                    <span className="block text-red-100">Reden: {customer.blacklistReden}</span>
                  )}
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setBlacklistDialog('clear')}
                    className="ml-auto self-center flex-shrink-0 px-2.5 py-1 text-xs font-medium rounded-md bg-white/15 text-white hover:bg-white/25 transition-colors"
                  >
                    Van blacklist halen
                  </button>
                )}
              </div>
            )}

            {/* Header */}
            <div className={clsx(
              'border-b border-slate-200 bg-white',
              isDrawer ? 'px-4 py-3' : 'px-6 py-4'
            )}>
              {/* Back button — page variant only */}
              {!isDrawer && (
                <button
                  onClick={() => navigate('/klanten')}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Klanten
                </button>
              )}

              {/* Customer info */}
              <div className="flex items-start gap-4">
                {/* Avatar — page variant only (drawer already has name in sticky header) */}
                {!isDrawer && (
                  <div className={clsx(
                    'w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0',
                    customer.type === 'account'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  )}>
                    <span className="text-xl font-semibold">
                      {customer.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  {!isDrawer && (
                    <div className="flex items-center gap-2 mb-1">
                      <h1 className="text-xl font-semibold text-slate-900">{customer.name}</h1>
                      <span className={clsx(
                        'text-xs px-2 py-0.5 rounded font-medium uppercase tracking-wide',
                        customer.type === 'account'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      )}>
                        {customer.type === 'account' ? 'Klant' : 'Lead'}
                      </span>
                    </div>
                  )}

                  {/* Contact details */}
                  <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                    {customer.email && (
                      <a href={`mailto:${customer.email}`} className="flex items-center gap-1 hover:text-indigo-600">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                        <span className="truncate max-w-[200px]">{customer.email}</span>
                      </a>
                    )}
                    {customer.phone && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                        </svg>
                        <PhoneNumber
                          number={customer.phone}
                          brand="KLEDA"
                          customerId={customer.id}
                          customerName={customer.name}
                          sourceContext="klant-detail"
                          className="hover:text-indigo-600"
                        />
                        <CallButton
                          number={customer.phone}
                          brand="KLEDA"
                          customerId={customer.id}
                          customerName={customer.name}
                          sourceContext="klant-detail"
                          variant="icon"
                          title="Bellen via office-app"
                        />
                        {customer.type === 'account' && (
                          <button
                            type="button"
                            onClick={() => gegevensRef.current?.focusContact()}
                            title="Bewerk contactgegevens"
                            aria-label="Bewerk contactgegevens"
                            className="text-slate-300 hover:text-indigo-600"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                            </svg>
                          </button>
                        )}
                      </span>
                    )}
                    {(customer.street || customer.city) && (
                      <span className="flex items-center gap-1 text-slate-500">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                        </svg>
                        {[customer.street, customer.postcode, customer.city].filter(Boolean).join(', ')}
                        {customer.type === 'account' && (
                          <button
                            type="button"
                            onClick={() => gegevensRef.current?.focusAddress()}
                            title="Bewerk adres"
                            aria-label="Bewerk adres"
                            className="text-slate-300 hover:text-indigo-600"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                            </svg>
                          </button>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                    {customer.createdDate && (
                      <span>Klant sinds {formatDate(customer.createdDate)}</span>
                    )}
                    {/* Betaalt op factuur — accounteigenschap (Betalingswijze 'Factuur'):
                        nieuwe orders krijgen Op_Factuur automatisch, lopende volgen mee.
                        Alleen zakelijk; particulieren zetten het per order in de Betaling-card. */}
                    {isBusinessAccount && (
                      <OpFactuurKlantToggle
                        accountName={customer.name}
                        actief={customer.betaaltOpFactuur === true}
                        betalingstermijn={customer.betalingstermijn}
                        canEdit={isAdmin || role === 'medewerker'}
                      />
                    )}
                    {totalOrders > 0 && (
                      <span>{totalOrders} order{totalOrders !== 1 ? 's' : ''}</span>
                    )}
                    {totalRevenue > 0 && (
                      <span>Totaal {formatCurrency(totalRevenue)}</span>
                    )}
                    <a
                      href={`${SF_BASE_URL}/${customer.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                      Salesforce
                    </a>
                    {customer.type === 'lead' && (
                      <Link
                        to={`/offertes/${customer.id}`}
                        className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Bekijk offerte →
                      </Link>
                    )}
                    <button
                      onClick={handleNewOrder}
                      className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Nieuwe order
                    </button>
                    {customer.type === 'account' && (
                      <button
                        onClick={() => setNewOfferteOpen(true)}
                        title="Nieuwe offerte voor deze klant"
                        className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Nieuwe offerte
                      </button>
                    )}
                    <MaakOpvolgtaakButton
                      customerId={customer.id}
                      customerName={customer.name}
                      defaultTitle={`Opvolgen: ${customer.name}`}
                      className="flex items-center gap-1 px-2.5 py-1 bg-white border border-slate-200 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-50 transition-colors"
                    />
                    {customer.type === 'account' && (
                      <button
                        onClick={handleWebshopOrder}
                        title="Nalevering / interne webshop-bestelling"
                        className="flex items-center gap-1 px-2.5 py-1 bg-violet-600 text-white text-xs font-medium rounded-md hover:bg-violet-700 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Webshop order
                      </button>
                    )}
                    {customer.type === 'account' && (
                      <button
                        onClick={() => setNewInvoiceOpen(true)}
                        title="Losse factuur met vrije regels (omschrijving + prijs)"
                        className="flex items-center gap-1 px-2.5 py-1 bg-sky-600 text-white text-xs font-medium rounded-md hover:bg-sky-700 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Nieuwe factuur
                      </button>
                    )}
                    {customer.type === 'account' && isB2BInkooporderAccount(customer.id) && (
                      <button
                        onClick={() => setInkooporderOpen(true)}
                        className="flex items-center gap-1 px-2.5 py-1 bg-amber-600 text-white text-xs font-medium rounded-md hover:bg-amber-700 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Inkooporder verwerken
                      </button>
                    )}
                    {customer.type === 'account' && isAtlasInkooporderAccount(customer.id) && (
                      <button
                        onClick={() => setAtlasInkooporderOpen(true)}
                        title="AtlasKidtech bestelbon (PDF) verwerken"
                        className="flex items-center gap-1 px-2.5 py-1 bg-amber-600 text-white text-xs font-medium rounded-md hover:bg-amber-700 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Inkooporder verwerken (AK)
                      </button>
                    )}
                    <button
                      onClick={handleSendEmail}
                      disabled={!canSendEmail}
                      title={!canSendEmail ? 'Geen e-mailadres bekend' : 'Stuur e-mail'}
                      className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white text-xs font-medium rounded-md hover:bg-indigo-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                      Stuur e-mail
                    </button>
                    {/* Marketing-opt-out: lead + PersonAccount op klant-niveau.
                        Zakelijk Account → opt-out per Contactpersoon (ContactsSection). */}
                    {(customer.type === 'lead' || (customer.type === 'account' && customer.isPersonAccount === true)) && (
                      <button
                        onClick={() => setConsentTarget({
                          recordType: customer.type === 'lead' ? 'lead' : 'account',
                          recordId: customer.id,
                          label: customer.name,
                          currentlyOptedOut: customer.emailOptOut === true,
                        })}
                        title={customer.emailOptOut
                          ? 'Wil geen marketing-mails — klik om weer aan te zetten'
                          : 'Marketing-mails uitzetten (AVG-opt-out)'}
                        className={clsx(
                          'flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors border',
                          customer.emailOptOut
                            ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                            : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700 hover:bg-slate-50'
                        )}
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                        </svg>
                        {customer.emailOptOut ? 'Geen marketing-mail' : 'Marketing-mail uit'}
                      </button>
                    )}
                    {/* Samenvoegen — admin-only, dubbele PersonAccounts mergen. */}
                    {isAdmin && customer.type === 'account' && customer.isPersonAccount === true && (
                      <button
                        onClick={() => setMergeDialogOpen(true)}
                        title="Dubbele klant samenvoegen — historie verhuist naar één account"
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-slate-200 bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8l-4 4 4 4M17 8l4 4-4 4M3 12h6m6 0h6" />
                        </svg>
                        Samenvoegen…
                      </button>
                    )}
                    {/* Blacklist — admin + medewerker, geen zaken meer doen met deze klant.
                        Medewerker staat aan de balie/telefoon en loopt tegen de lastige klant
                        aan, dus die moet het zelf kunnen vastleggen; de verplichte reden (min.
                        BLACKLIST_REDEN_MIN tekens) + audit-regel houden het navolgbaar.
                        "Van blacklist halen" zit in de rode banner hierboven. */}
                    {customer.type === 'account' && !customer.blacklisted && (
                      <button
                        onClick={() => setBlacklistDialog('set')}
                        title="Op de blacklist zetten — nieuwe orders/offertes worden geweigerd"
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        Op blacklist zetten
                      </button>
                    )}
                  </div>
                </div>
              </div>

            </div>

            {/* Scrollable body: contactpersonen + klantgegevens cards + sticky tabs + tab content.
                Contactpersonen staat in de scroll (niet in de vaste kop) zodat de lijst bij
                bedrijven met veel contacten wegscrollt en de tabs + inhoud de volle hoogte krijgen. */}
            <div className="flex-1 overflow-y-auto" data-drawer-scroll={isDrawer ? '' : undefined}>
              {/* Business Account — contactpersonen (+ knop om contact toe te voegen) */}
              {isBusinessAccount && (
                <ContactsSection
                  contacts={activeCustomer?.contacts || []}
                  canAddContact={true}
                  onAddContact={() => setAddContactDialogOpen(true)}
                  onEditContact={(c) => setEditingContact(c)}
                  onDeleteContact={(c) => setDeletingContact(c)}
                  onToggleConsent={(c) => setConsentTarget({
                    recordType: 'contact',
                    recordId: c.id,
                    label: c.name,
                    currentlyOptedOut: c.emailOptOut === true,
                  })}
                  onOpenContact={(c) => setDrawerContactId(c.id)}
                />
              )}

              {/* Klantgegevens (edit) — alleen voor accounts */}
              {customer.type === 'account' && (
                <div className={clsx(isDrawer ? 'px-4' : 'px-6')}>
                  <KlantgegevensCard ref={gegevensRef} customer={customer} compact={isDrawer} />
                </div>
              )}

              {/* Service-abonnementen — alleen voor accounts */}
              {customer.type === 'account' && (
                <div className={clsx('mt-4', isDrawer ? 'px-4' : 'px-6')}>
                  <AbonnementenSection accountId={customer.id} orders={activeCustomer?.orders || []} />
                </div>
              )}

              {/* Terugkom-moment (volgende reiniging, plan 2026-10-22) — alleen voor accounts */}
              {customer.type === 'account' && (
                <div className={clsx('mt-4', isDrawer ? 'px-4' : 'px-6')}>
                  <TerugkomMomentKaart accountId={customer.id} orders={activeCustomer?.orders || []} />
                </div>
              )}

              {/* Sticky tabs */}
              <div className={clsx(
                'sticky top-0 z-10 bg-white border-b border-slate-200 flex gap-1 flex-wrap',
                isDrawer ? 'px-4 py-2 mt-4' : 'px-6 py-2 mt-4'
              )}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={clsx(
                      'px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5',
                      activeTab === tab.id
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-slate-500 hover:bg-slate-50'
                    )}
                  >
                    {tab.label}
                    {tab.count !== undefined && tab.count > 0 && (
                      <span className={clsx(
                        'text-xs px-1.5 py-0.5 rounded-full',
                        activeTab === tab.id ? 'bg-indigo-100' : 'bg-slate-100'
                      )}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div>
              {activeTab === 'tijdlijn' && (
                <TimelineTab
                  timeline={activeCustomer?.timeline || []}
                  onOpenItem={(item) => setTimelineDrawerItem(item)}
                  onOpenCall={(callId) => openCall(callId)}
                />
              )}
              {activeTab === 'orders' && (
                <OrdersTab
                  orders={activeCustomer?.orders || []}
                  onReorder={handleReorder}
                  onOpenOrder={openOrderDrawer}
                  onOpenContact={(contactId) => setDrawerContactId(contactId)}
                  cyclusAccount={customer?.type === 'account' && isB2BCyclusAccount(customer.id)}
                  onOrdersChanged={() => fetchCustomerDetail(customerId, type)}
                />
              )}
              {activeTab === 'offertes' && (
                <OffertesTab
                  leads={activeCustomer?.leads || []}
                  onOpenOfferte={openOfferteDrawer}
                />
              )}
              {activeTab === 'doorverwijzingen' && (
                <CincoPlatformTab referrals={cincoReferrals} onOpenReferral={openOpportunityDrawer} />
              )}
              {activeTab === 'werkaanvragen' && (
                <WerkaanvragenTab werkaanvragen={activeCustomer?.werkaanvragen || activeCustomer?.orders || []} />
              )}
              {activeTab === 'berichten' && (
                <BerichtenTab conversations={activeCustomer?.conversations || []} />
              )}
              {activeTab === 'telefoon' && (
                <TelefoonTab customerId={customerId} />
              )}
              {activeTab === 'emails' && (
                <EmailsTab
                  emails={activeCustomer?.emails || []}
                  onOpenEmail={(id) => setTimelineDrawerItem({ contentKind: 'sf_email', contentRef: id })}
                  onOpenOrder={openOrderDrawer}
                />
              )}
              {activeTab === 'facturen' && (
                <InvoicesTab invoices={activeCustomer?.invoices || []} onOpenInvoice={openInvoiceDrawer} />
              )}
              {activeTab === 'marketing' && customer.type === 'account' && (
                <KlantMarketingTab
                  sfAccountId={customer.id}
                  onOpenMail={(send) =>
                    setTimelineDrawerItem({
                      contentKind: 'marketing',
                      contentRef: send.id,
                      subject: send.subject ?? undefined,
                      snippet: send.via ?? undefined,
                      status: send.status,
                      date: send.date ?? undefined,
                    })
                  }
                />
              )}
              </div>
            </div>
          </>
        )}
      </div>
      <OrderPanel />
      <TimelineItemDrawer
        item={timelineDrawerItem}
        onClose={() => setTimelineDrawerItem(null)}
      />
      <ContactDrawer
        contactId={drawerContactId}
        onClose={() => setDrawerContactId(null)}
        onOpenOrder={(orderId) => openOrderDrawer(orderId)}
        onEdit={(d) => {
          setDrawerContactId(null);
          setEditingContact({
            id: d.id,
            name: d.name,
            email: d.email,
            phone: d.phone,
            title: d.title,
            department: d.department,
            emailOptOut: d.emailOptOut,
          });
        }}
      />
      {customer && (
        <OutboundComposerModal
          open={composerOpen}
          onClose={() => setComposerOpen(false)}
          customer={{
            id: customer.id,
            name: customer.name,
            email: customer.email,
            phone: customer.phone,
          }}
          to={composerTo}
          contacts={emailContacts.map((c) => ({ id: c.id, name: c.name, email: c.email }))}
          orders={activeCustomer?.orders}
        />
      )}
      {customer && (
        <>
          <ContactPickerDialog
            isOpen={contactPickerOpen}
            accountName={customer.name}
            contacts={activeCustomer?.contacts || []}
            onSelect={(contact) => {
              setContactPickerOpen(false);
              if (pendingMode) {
                proceedWithContact(contact, pendingMode, pendingReorderItems || undefined);
              }
              setPendingMode(null);
              setPendingReorderItems(null);
            }}
            onCancel={() => {
              setContactPickerOpen(false);
              setPendingMode(null);
              setPendingReorderItems(null);
            }}
          />
          <ContactPickerDialog
            isOpen={emailPickerOpen}
            accountName={customer.name}
            subtitle={`Naar welke contactpersoon binnen ${customer.name} wil je mailen?`}
            contacts={emailContacts}
            onSelect={(contact) => {
              setEmailPickerOpen(false);
              setComposerTo(contact.email);
              setLastUsedContact(customer.id, contact.id);
              setComposerOpen(true);
            }}
            onCancel={() => setEmailPickerOpen(false)}
          />
          <MissingContactDialog
            isOpen={missingContactDialogOpen}
            accountId={customer.id}
            accountName={customer.name}
            onClose={() => setMissingContactDialogOpen(false)}
            onAdd={() => {
              setMissingContactDialogOpen(false);
              setAddContactDialogOpen(true);
            }}
          />
          <AddContactDialog
            isOpen={addContactDialogOpen}
            accountId={customer.id}
            accountName={customer.name}
            onClose={() => setAddContactDialogOpen(false)}
            onSuccess={async () => {
              setAddContactDialogOpen(false);
              await fetchCustomerDetail(customer.id, 'account');
            }}
          />
          <EditContactDialog
            isOpen={editingContact !== null}
            contact={editingContact}
            accountName={customer.name}
            onClose={() => setEditingContact(null)}
            onSuccess={async () => {
              setEditingContact(null);
              await fetchCustomerDetail(customer.id, 'account');
            }}
          />
          <DeleteContactDialog
            isOpen={deletingContact !== null}
            contact={deletingContact}
            accountName={customer.name}
            onClose={() => setDeletingContact(null)}
            onSuccess={async () => {
              setDeletingContact(null);
              await fetchCustomerDetail(customer.id, 'account');
            }}
          />
          <EmailConsentDialog
            target={consentTarget}
            onClose={() => setConsentTarget(null)}
          />
          <BlacklistDialog
            isOpen={blacklistDialog !== null}
            mode={blacklistDialog ?? 'set'}
            accountName={customer.name}
            onClose={() => setBlacklistDialog(null)}
            onSuccess={() => setBlacklistDialog(null)}
          />
          <MergeDialog
            isOpen={mergeDialogOpen}
            sourceAccountId={customer.id}
            sourceName={customer.name}
            onClose={() => setMergeDialogOpen(false)}
            onMerged={(masterId) => {
              setMergeDialogOpen(false);
              // Was het huidige record het duplicaat → het bestaat niet meer;
              // navigeer naar de master. Was het de master → herlaad het dossier.
              if (masterId !== customer.id) {
                navigate(`/klanten/${masterId}?type=account`);
              } else {
                fetchCustomerDetail(masterId, 'account');
              }
            }}
          />
          <NewOfferteDialog
            isOpen={newOfferteOpen}
            onClose={() => setNewOfferteOpen(false)}
            prefillCustomer={offertePrefill}
            onCreated={async (leadId) => {
              setNewOfferteOpen(false);
              openOfferteDrawer(leadId);
              await fetchCustomerDetail(customer.id, 'account');
            }}
          />
          <NewInvoiceDialog
            isOpen={newInvoiceOpen}
            onClose={() => setNewInvoiceOpen(false)}
            preset={{
              accountId: customer.id,
              name: customer.name,
              isBusiness: isBusinessAccount,
            }}
            onCreated={async (invoiceId) => {
              setNewInvoiceOpen(false);
              await fetchCustomerDetail(customer.id, 'account');
              openInvoiceDrawer(invoiceId);
            }}
          />
        </>
      )}
      {customer && isB2BInkooporderAccount(customer.id) && (
        <InkooporderWizardDialog
          accountId={customer.id}
          accountName={getB2BAccountName(customer.id) ?? customer.name}
          isOpen={inkooporderOpen}
          onClose={() => setInkooporderOpen(false)}
          onCreated={(workOrderId) => {
            setInkooporderOpen(false);
            fetchCustomerDetail(customerId, type);
            navigate(`/orders/${workOrderId}`);
          }}
        />
      )}
      {customer && isAtlasInkooporderAccount(customer.id) && (
        <AtlasInkooporderWizardDialog
          accountId={customer.id}
          accountName={ATLAS_KIDTECH_ACCOUNT_NAME}
          isOpen={atlasInkooporderOpen}
          openCycleOrders={activeCustomer?.orders || []}
          onClose={() => {
            setAtlasInkooporderOpen(false);
            // Afrondstap kan orders gewijzigd hebben (status/factuur) — vers laden.
            fetchCustomerDetail(customerId, type);
          }}
          onCreated={() => {
            // Wizard blijft open in de afrondstap; alleen de orderslijst verversen.
            fetchCustomerDetail(customerId, type);
          }}
          onOpenOrder={(workOrderId) => {
            setAtlasInkooporderOpen(false);
            navigate(`/orders/${workOrderId}`);
          }}
        />
      )}
    </>
  );
}
